/**
 * useInviteTab - Manages all state and logic for the Invites tab.
 *
 * Reads / writes UI state through the `useUserManagementStore` Zustand
 * store and composes it with data-fetching (TanStack Query) and
 * derived computations (filtered & paginated rows).
 *
 * @returns Invite tab state and handlers
 *
 * @example
 * ```ts
 * const invite = useInviteTab();
 * // invite.paginatedItems — rows for the current page
 * // invite.handleCancelInvite(item) — opens confirm + cancels
 * ```
 *
 * @module features/users/hooks/useInviteTab
 */
import { useMemo, useCallback } from 'react';

import { useUserInvites } from '@/features/users/hooks/useUserInvites';
import { useShallow } from 'zustand/react/shallow';
import { useUserManagementStore } from '@/features/users/store';
import type { InviteItem } from '@/features/users/types/users.types';

export const useInviteTab = () => {
  // ── Store ──────────────────────────────────────────────────────
  const {
    invitePage: page,
    invitePageSize: pageSize,
    selectedRowKeys,
    inviteFiltersOpen: filtersOpen,
    appliedInviteFilters: appliedFilters,
    draftInviteFilters: draftFilters,
    setInvitePage: setPage,
    setInvitePageSize: setPageSize,
    setSelectedRowKeys,
    setDraftInviteFilters: setDraftFilters,
    openInviteFilters: handleOpenFilters,
    closeInviteFilters: handleCloseFilters,
    applyInviteFilters: handleApplyFilters,
    clearInviteFilters: handleClearFilters,
    openCancelInviteModal,
    openResendInviteModal,
  } = useUserManagementStore(
    useShallow((s) => ({
      invitePage: s.invitePage,
      invitePageSize: s.invitePageSize,
      selectedRowKeys: s.selectedRowKeys,
      inviteFiltersOpen: s.inviteFiltersOpen,
      appliedInviteFilters: s.appliedInviteFilters,
      draftInviteFilters: s.draftInviteFilters,
      setInvitePage: s.setInvitePage,
      setInvitePageSize: s.setInvitePageSize,
      setSelectedRowKeys: s.setSelectedRowKeys,
      setDraftInviteFilters: s.setDraftInviteFilters,
      openInviteFilters: s.openInviteFilters,
      closeInviteFilters: s.closeInviteFilters,
      applyInviteFilters: s.applyInviteFilters,
      clearInviteFilters: s.clearInviteFilters,
      openCancelInviteModal: s.openCancelInviteModal,
      openResendInviteModal: s.openResendInviteModal,
    }))
  );

  // ── Data fetching (server-side pagination) ────────────────────
  const {
    data: invitesData,
    isLoading,
    isRefetching,
    refetch,
    error,
  } = useUserInvites({
    offset: page * pageSize,
    limit: pageSize,
    ...(appliedFilters.email ? { email: appliedFilters.email.trim() } : {}),
    ...(appliedFilters.statuses?.length
      ? { status: appliedFilters.statuses.join(',') }
      : {}),
  });
  const isUnauthorized = Boolean(
    error &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === 401
  );

  // ── Active filter count (derived) ─────────────────────────────
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.email && appliedFilters.email.trim().length > 0)
      count += 1;
    if (appliedFilters.statuses && appliedFilters.statuses.length > 0)
      count += 1;
    return count;
  }, [appliedFilters]);

  // ── Invite actions ─────────────────────────────────────────────
  const handleCancelInvite = useCallback(
    (invite: InviteItem) => {
      openCancelInviteModal(invite);
    },
    [openCancelInviteModal]
  );

  const handleResendInvite = useCallback(
    (invite: InviteItem) => {
      openResendInviteModal(invite);
    },
    [openResendInviteModal]
  );

  // ── Refresh (re-fetch the current page) ───────────────────────
  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    // Data
    invitesData,
    isLoading,
    error,
    isUnauthorized,
    isRefreshing: isRefetching,
    handleRefresh,

    // Pagination
    page,
    pageSize,
    setPage,
    setPageSize,

    // Selection
    selectedRowKeys,
    setSelectedRowKeys,

    // Filters
    filtersOpen,
    draftFilters,
    setDraftFilters,
    appliedFilters,
    activeFiltersCount,
    handleOpenFilters,
    handleCloseFilters,
    handleClearFilters,
    handleApplyFilters,

    // Invite actions
    handleCancelInvite,
    handleResendInvite,
  } as const;
};
